import { useEffect } from "react";
import { useForm } from "@inertiajs/react";
import { handleDrawerSuccess, hasArrayErrors } from "@/lib/drawer-submission";
import { manage_user_path } from "@/routes";

function roleLabel(role = "") {
  if (!role) return "Ingen rolle";

  return role.replace(/Role$/, "").replace(/([a-z])([A-Z])/g, "$1 $2");
}

export default function UserEditDrawer({ user = {}, organizations = [], errors = [], onSuccess, onProcessingChange = null }) {
  const { data, setData, patch, processing } = useForm({
    user: {
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      email: user.email || "",
      organization_ids: user.organization_ids || []
    }
  });

  useEffect(() => {
    setData({
      user: {
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.email || "",
        organization_ids: user.organization_ids || []
      }
    });
  }, [user, setData]);

  useEffect(() => {
    onProcessingChange?.(processing);
  }, [onProcessingChange, processing]);

  const toggleOrganization = (organizationId) => {
    const organizationIds = data.user.organization_ids || [];

    setData("user", {
      ...data.user,
      organization_ids: organizationIds.includes(organizationId)
        ? organizationIds.filter((id) => id !== organizationId)
        : [...organizationIds, organizationId]
    });
  };

  const submit = (event) => {
    event.preventDefault();

    patch(manage_user_path(user.id), {
      preserveScroll: true,
      onSuccess: (page) => handleDrawerSuccess(
        page,
        (props) => props.show_edit_user_drawer || hasArrayErrors(props, "edit_errors"),
        onSuccess
      )
    });
  };

  return (
    <form id="manage-user-edit-form" className="vstack gap-4" onSubmit={submit}>
      {errors.length > 0 ? (
        <div className="alert alert-danger mb-0">{errors.join(", ")}</div>
      ) : null}

      <div className="row g-3">
        <div className="col-md-6">
          <label className="form-label fw-semibold">Fornavn</label>
          <input
            type="text"
            className="form-control"
            value={data.user.first_name}
            onChange={(event) => setData("user", { ...data.user, first_name: event.target.value })}
            required
          />
        </div>

        <div className="col-md-6">
          <label className="form-label fw-semibold">Efternavn</label>
          <input
            type="text"
            className="form-control"
            value={data.user.last_name}
            onChange={(event) => setData("user", { ...data.user, last_name: event.target.value })}
            required
          />
        </div>

        <div className="col-12">
          <label className="form-label fw-semibold">E-mail</label>
          <input
            type="email"
            className="form-control"
            value={data.user.email}
            onChange={(event) => setData("user", { ...data.user, email: event.target.value })}
            required
          />
        </div>
      </div>

      <div className="vstack gap-3">
        <div>
          <label className="form-label fw-semibold mb-1">Organisationer</label>
          <p className="text-muted mb-0">
            Vælg hvilke organisationer brugeren skal have adgang til.
          </p>
        </div>

        <div className="vstack gap-2">
          {organizations.map((organization) => {
            const membership = user.memberships?.find((item) => item.organization_id === organization.id);
            const checked = (data.user.organization_ids || []).includes(organization.id);

            return (
              <label key={organization.id} className="border rounded-4 p-3 d-flex align-items-start gap-3">
                <input
                  type="checkbox"
                  className="form-check-input mt-1"
                  checked={checked}
                  onChange={() => toggleOrganization(organization.id)}
                />
                <span className="d-block">
                  <span className="d-block fw-semibold text-dark">{organization.name}</span>
                  <span className="d-block text-muted small">
                    {membership
                      ? `${membership.active ? "Aktiv organisation" : "Har adgang"}${membership.role ? ` • ${roleLabel(membership.role)}` : ""}`
                      : "Ingen adgang endnu"}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      </div>

      <div className="d-flex justify-content-end">
        <button type="submit" className="btn btn-primary" disabled={processing}>
          {processing ? "Gemmer..." : "Gem ændringer"}
        </button>
      </div>
    </form>
  );
}
